import { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, Copy, Check, Send } from "lucide-react";
import DashboardLayout from "../../layouts/DashboardLayout";
import MessagingSwitches from "../../components/admin/MessagingSwitches";
import api from "../../api";

// NL Labs: one DLT-registered SMS template per message flow (backend
// notifications/sms_templates.py, table sms_templates). A flow sends only once
// it's switched on here with its MSG91 template id.
const STATUS = {
  ready: { label: "Sending", tone: "bg-emerald-100 text-emerald-800" },
  disabled: { label: "Off", tone: "bg-secondary-100 text-secondary-700" },
  channel_off: { label: "SMS off", tone: "bg-secondary-100 text-secondary-700" },
  no_template_id: { label: "Needs MSG91 id", tone: "bg-amber-100 text-amber-800" },
  no_gateway: { label: "MSG91 not set up", tone: "bg-amber-100 text-amber-800" },
};
const AUDIENCE = { patient: "To patients", staff: "To staff", clinic: "To the clinic" };

function CopyButton({ text, label }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text || "");
      setDone(true);
      setTimeout(() => setDone(false), 1500);
    } catch { /* text stays selectable */ }
  };
  return (
    <button type="button" onClick={copy} className="inline-flex items-center gap-1 text-xs text-primary-700 hover:underline">
      {done ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {label}
    </button>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block normal-case font-sans tracking-normal">
      <span className="text-sm font-medium text-secondary-800">{label}</span>
      {hint && <span className="block text-xs text-secondary-500">{hint}</span>}
      <div className="mt-1">{children}</div>
    </label>
  );
}

const inputCls = "w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm text-secondary-900";

function TemplateCard({ t, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [phone, setPhone] = useState("");

  useEffect(() => {
    setForm({
      template_name: t.template_name || "",
      dlt_template_id: t.dlt_template_id || "",
      msg91_template_id: t.msg91_template_id || "",
      content: t.content || "",
      notes: t.notes || "",
      variable_names: Object.fromEntries(t.variables.map((v) => [v.key, v.msg91_name])),
    });
  }, [t]);

  const save = async (data, okText) => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await api.saveSmsTemplate(t.flow_key, data);
      onSaved(r.data);
      setMsg({ ok: true, text: okText });
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.error || "Couldn't save." });
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setBusy(true);
    setMsg(null);
    try {
      await api.testSmsTemplate(t.flow_key, phone);
      setMsg({ ok: true, text: "Test SMS sent with the sample values." });
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.error || "Couldn't send the test." });
    } finally {
      setBusy(false);
    }
  };

  const status = STATUS[t.status] || STATUS.disabled;
  return (
    <div className="rounded-xl border border-secondary-200 bg-white">
      <div className="flex flex-wrap items-center gap-3 p-4">
        <button type="button" onClick={() => setOpen(!open)} className="flex-1 min-w-[12rem] text-left">
          <p className="font-semibold text-secondary-900 flex items-center gap-2">
            {t.label} {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </p>
          <p className="text-xs text-secondary-500 mt-0.5">
            {AUDIENCE[t.audience]} · {t.template_name}
            {t.msg91_template_id ? ` · MSG91 ${t.msg91_template_id}` : ""}
          </p>
        </button>
        {!t.live && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-sky-100 text-sky-800" title="Ids can be saved now; the app doesn't send this message yet.">
            Not sent by the app yet
          </span>
        )}
        {t.throttled_today > 0 && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-800" title="Blocked by the sending limits today. In-app and email copies still went.">
            {t.throttled_today} held back today
          </span>
        )}
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${status.tone}`}>{status.label}</span>
        <label className="inline-flex items-center gap-2 normal-case font-sans tracking-normal text-sm text-secondary-700 cursor-pointer">
          <input type="checkbox" className="h-4 w-4" checked={t.enabled} disabled={busy}
            onChange={(e) => save({ enabled: e.target.checked }, e.target.checked ? "Switched on." : "Switched off.")} />
          On
        </label>
      </div>
      {msg && !open && <p className={`px-4 pb-3 text-sm ${msg.ok ? "text-emerald-700" : "text-red-600"}`}>{msg.text}</p>}

      {open && form && (
        <div className="border-t border-secondary-200 p-4 space-y-4">
          <div className="rounded-lg bg-secondary-50 p-3 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-medium text-secondary-800">Wording to register on DLT</p>
              <CopyButton text={form.content} label="Copy" />
            </div>
            <p className="text-sm text-secondary-800 whitespace-pre-wrap break-words font-mono">{form.content}</p>
            <p className="text-xs text-secondary-500">Preview: {t.preview}</p>
            <p className="text-xs text-secondary-500">
              Limit: {t.kind === "code" ? "login codes" : t.kind === "crisis" ? "crisis alerts" : "messages"} count towards this number's{" "}
              {t.kind === "message" ? "daily cap" : "hourly and daily caps"}
              {t.gap_minutes ? `; the same message goes to the same number at most once every ${t.gap_minutes} minutes` : ""}.
            </p>
            {t.justification && (
              <p className="text-xs text-secondary-600">
                3 variables - justification for DLT: "{t.justification}" <CopyButton text={t.justification} label="Copy" />
              </p>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Template name" hint="As named on DLT and MSG91">
              <input className={inputCls} value={form.template_name} onChange={(e) => setForm({ ...form, template_name: e.target.value })} />
            </Field>
            <Field label="DLT template id" hint="From the operator's portal">
              <input className={inputCls} value={form.dlt_template_id} onChange={(e) => setForm({ ...form, dlt_template_id: e.target.value })} />
            </Field>
            <Field label="MSG91 template id" hint="Used to send">
              <input className={inputCls} value={form.msg91_template_id} onChange={(e) => setForm({ ...form, msg91_template_id: e.target.value })} />
            </Field>
          </div>

          {t.variables.length > 0 && (
            <div>
              <p className="text-sm font-medium text-secondary-800">Variables, in order</p>
              <p className="text-xs text-secondary-500">Enter each one's name as MSG91 shows it in the template (without the ##).</p>
              <div className="mt-2 grid gap-3 sm:grid-cols-3">
                {t.variables.map((v) => (
                  <Field key={v.key} label={`${v.label} {#${v.tag}#}`} hint={`Sample: ${v.sample}`}>
                    <input className={inputCls} value={form.variable_names[v.key] || ""}
                      onChange={(e) => setForm({ ...form, variable_names: { ...form.variable_names, [v.key]: e.target.value } })} />
                  </Field>
                ))}
              </div>
            </div>
          )}

          <Field label="Wording as approved" hint="Change only if DLT approved different wording. Keep the same variables in the same order.">
            <textarea rows={3} className={inputCls} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} />
          </Field>
          {form.content !== t.suggested_content && (
            <button type="button" className="text-xs text-primary-700 hover:underline" onClick={() => setForm({ ...form, content: t.suggested_content })}>
              Go back to the suggested wording
            </button>
          )}

          <Field label="Notes">
            <textarea rows={2} className={inputCls} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="e.g. submitted on Jio 31 Oct, approved 2 Nov" />
          </Field>

          <div className="flex flex-wrap items-center gap-3">
            <button type="button" disabled={busy} onClick={() => save(form, "Saved.")}
              className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium disabled:opacity-50">
              Save
            </button>
            <div className="flex items-center gap-2">
              <input className="rounded-lg border border-secondary-300 px-3 py-2 text-sm w-40" placeholder="Mobile number"
                value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="numeric" />
              <button type="button" disabled={busy || !phone} onClick={test}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-secondary-300 text-sm text-secondary-800 disabled:opacity-50">
                <Send className="w-4 h-4" /> Send test
              </button>
            </div>
          </div>
          {msg && <p className={`text-sm ${msg.ok ? "text-emerald-700" : "text-red-600"}`}>{msg.text}</p>}
        </div>
      )}
    </div>
  );
}

const KIND_LABEL = { code: "Login and sign-up codes", crisis: "Crisis alerts to staff", message: "All other messages" };

function LimitsCard({ data, onSaved }) {
  const [values, setValues] = useState(() => Object.fromEntries(data.limits.map((l) => [l.key, l.value])));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const save = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await api.saveSmsLimits(values);
      onSaved(r.data);
      setMsg({ ok: true, text: "Saved." });
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.error || "Couldn't save." });
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="rounded-xl border border-secondary-200 bg-white p-4 space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-secondary-900">Sending limits</h2>
        <p className="text-sm text-secondary-500 mt-1">
          Every SMS is checked against these before it goes, so a misused key, a repeated button or a bug can't flood anyone.
          A held-back SMS is logged; the in-app and email copies of the alert still go.
        </p>
      </div>
      <div>
        <p className="text-sm font-medium text-secondary-800">Per receiving number (fixed)</p>
        <ul className="mt-1 text-sm text-secondary-700 space-y-0.5">
          {Object.entries(data.per_phone).map(([kind, caps]) => (
            <li key={kind}>{KIND_LABEL[kind]}: {caps.map((c) => `${c.max} a ${c.window}`).join(", ")}</li>
          ))}
        </ul>
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        {data.limits.map((l) => (
          <Field key={l.key} label={l.label} hint={`Default ${l.default}`}>
            <input type="number" min={1} className={inputCls} value={values[l.key] ?? ""}
              onChange={(e) => setValues({ ...values, [l.key]: e.target.value })} />
          </Field>
        ))}
      </div>
      <div className="flex items-center justify-end gap-3">
        {msg && <p className={`text-sm ${msg.ok ? "text-emerald-700" : "text-red-600"}`}>{msg.text}</p>}
        <button type="button" disabled={busy} onClick={save}
          className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium disabled:opacity-50">Save limits</button>
      </div>
    </section>
  );
}

export default function AdminSmsTemplates() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = () => api.fetchSmsTemplates()
    .then((r) => setData(r.data))
    .catch((e) => setError(e?.response?.data?.error || "Couldn't load SMS templates."));

  useEffect(() => { load(); }, []);

  const onSaved = (row) => setData((d) => ({ ...d, templates: d.templates.map((t) => (t.flow_key === row.flow_key ? row : t)) }));

  return (
    <DashboardLayout>
      <div className="container mx-auto p-4 md:p-8 max-w-5xl space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">SMS Templates</h1>
          <p className="text-sm text-gray-500 mt-1">
            Every SMS the platform sends, one DLT template each. A message goes out only when it's switched on here with its
            MSG91 template id, and for patient and crisis alerts, when the clinic has SMS allowed too.
          </p>
        </div>

        {error && <p className="text-red-600">{error}</p>}
        {!data && !error && <p className="text-secondary-500">Loading…</p>}
        {data && !data.gateway_ready && (
          <p className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900">
            MSG91 isn't set up on this server (MSG91_AUTH_KEY and MSG91_SENDER_ID), so nothing will send yet. You can still save the ids.
          </p>
        )}

        {data && <MessagingSwitches channel="sms" switches={data.switches} onChange={() => load()} />}

        {data?.limits && data?.per_phone && <LimitsCard data={data} onSaved={setData} />}

        {data && data.groups.map((g) => {
          const rows = data.templates.filter((t) => t.group === g.key);
          if (!rows.length) return null;
          return (
            <section key={g.key} className="space-y-3">
              <h2 className="text-lg font-semibold text-secondary-900">{g.label}</h2>
              {rows.map((t) => <TemplateCard key={t.flow_key} t={t} onSaved={onSaved} />)}
            </section>
          );
        })}
      </div>
    </DashboardLayout>
  );
}
